import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTickets, updateTicket } from '../../entities/ticket/api/ticketApi';
import { Ticket, TicketStatus, TicketPriority, TicketSite } from '../../entities/ticket';
import { AuthStorage } from '../../features/auth/infrastructure/authStorage';
import * as S from './DashboardPage.styles';

type KanbanColumn = {
  id: TicketStatus | 'waiting_response';
  label: string;
  statuses: TicketStatus[];
  isActive: boolean;
};

const kanbanColumns: KanbanColumn[] = [
  { id: 'new', label: 'Новые', statuses: ['new'], isActive: true },
  { id: 'active', label: 'В работе', statuses: ['active', 'waiting_info'], isActive: true },
  { id: 'waiting_response', label: 'Ожидаем ответ клиента', statuses: ['active', 'waiting_info'], isActive: true },
  { id: 'in_programming', label: 'В программирование', statuses: ['in_programming'], isActive: true },
  { id: 'resolved', label: 'Решено', statuses: ['resolved'], isActive: false },
];

const MAX_TICKETS_PER_COLUMN = 5;

export const DashboardPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedTicket, setDraggedTicket] = useState<Ticket | null>(null);
  const [draggedOverColumn, setDraggedOverColumn] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [filters, setFilters] = useState({
    search: '',
    site: '' as TicketSite | '',
    priority: '' as TicketPriority | '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    const authStorage = new AuthStorage();
    const user = authStorage.getUser();
    if (user?.email) {
      setUserEmail(user.email);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [filters.search, filters.site, filters.priority]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await getTickets({
        search: filters.search || undefined,
        site: filters.site || undefined,
        priority: filters.priority || undefined,
      });
      setTickets(data);
    } catch (error) {
      console.error('Ошибка загрузки тикетов:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTicketsForColumn = (column: KanbanColumn): Ticket[] => {
    let columnTickets: Ticket[] = [];
    
    if (column.id === 'waiting_response') {
      columnTickets = tickets.filter(ticket => {
        if (ticket.status === 'waiting_info') {
          return true;
        }
        if (ticket.status === 'active') {
          const lastMessage = ticket.lastMessage;
          return lastMessage && 
                 lastMessage.direction === 'incoming' && 
                 !lastMessage.isInternal;
        }
        return false;
      });
    } else if (column.id === 'new') {
      columnTickets = tickets.filter(t => t.status === 'new');
    } else if (column.id === 'active') {
      columnTickets = tickets.filter(t => {
        if (t.status === 'active') {
          const lastMessage = t.lastMessage;
          const isWaitingResponse = lastMessage && 
                                    lastMessage.direction === 'incoming' && 
                                    !lastMessage.isInternal;
          return !isWaitingResponse;
        }
        if (t.status === 'waiting_info') {
          return false;
        }
        return false;
      });
    } else {
      columnTickets = tickets.filter(t => {
        return column.statuses.includes(t.status);
      });
    }

    if (column.isActive) {
      return columnTickets
        .sort((a: Ticket, b: Ticket) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .slice(0, MAX_TICKETS_PER_COLUMN);
    } else {
      return columnTickets
        .sort((a: Ticket, b: Ticket) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())
        .slice(0, MAX_TICKETS_PER_COLUMN);
    }
  };

  const handleDragStart = (e: React.DragEvent, ticket: Ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDraggedOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, column: KanbanColumn) => {
    e.preventDefault();
    setDraggedOverColumn(null);

    if (!draggedTicket) return;

    if (column.id === 'waiting_response') {
      setDraggedTicket(null);
      return;
    }

    let newStatus: TicketStatus;
    
    if (column.id === 'new') {
      newStatus = 'new';
    } else if (column.id === 'active') {
      newStatus = draggedTicket.status === 'waiting_info' ? 'waiting_info' : 'active';
    } else if (column.id === 'resolved') {
      newStatus = 'resolved';
    } else if (column.id === 'closed') {
      newStatus = 'closed';
    } else {
      setDraggedTicket(null);
      return;
    }

    if (draggedTicket.status !== newStatus) {
      try {
        console.log(`Обновление тикета ${draggedTicket.id}: ${draggedTicket.status} -> ${newStatus}`);
        await updateTicket(draggedTicket.id, { status: newStatus });
        setTickets(prevTickets => 
          prevTickets.map(ticket => 
            ticket.id === draggedTicket.id 
              ? { ...ticket, status: newStatus }
              : ticket
          )
        );
        await loadTickets();
      } catch (error) {
        console.error('Ошибка обновления статуса:', error);
        alert('Не удалось изменить статус тикета');
        await loadTickets();
      }
    }

    setDraggedTicket(null);
  };

  const getPriorityColor = (priority: TicketPriority): string => {
    switch (priority) {
      case 'urgent': return '#EF4444';
      case 'high': return '#F59E0B';
      case 'medium': return '#8B5CF6';
      case 'low': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getPriorityLabel = (priority: TicketPriority): string => {
    switch (priority) {
      case 'urgent': return 'Срочно';
      case 'high': return 'Высокий';
      case 'medium': return 'Средний';
      case 'low': return 'Низкий';
      default: return priority;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `Сегодня ${hours}:${minutes}`;
    } else if (diffDays === 1) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `Вчера ${hours}:${minutes}`;
    } else {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}.${month} ${hours}:${minutes}`;
    }
  };

    const stats = {
    total: tickets.length,
    new: tickets.filter((t: Ticket) => t.status === 'new').length,
    active: tickets.filter((t: Ticket) => t.status === 'active' || t.status === 'waiting_info').length,
    waitingResponse: tickets.filter((t: Ticket) => {
      const lastMessage = t.lastMessage;
      return lastMessage && lastMessage.direction === 'incoming' && !lastMessage.isInternal;
    }).length,
    resolved: tickets.filter((t: Ticket) => t.status === 'resolved').length,
    closed: tickets.filter((t: Ticket) => t.status === 'closed' || t.status === 'spam' || t.status === 'duplicate').length,
  };

  const handleTicketClick = (ticketId: string) => {
    navigate(`/tickets?ticket=${ticketId}`);
  };

  const handleFilterChange = (field: 'search' | 'site' | 'priority', value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <S.DashboardContainer>
      <S.DashboardHeader>
        <S.DashboardGreeting>
          {userEmail ? `Привет, ${userEmail}` : 'Добро пожаловать в CRM'}
        </S.DashboardGreeting>
        <S.HeaderActions>
          <S.SearchInput
            type="text"
            placeholder="Поиск тикетов..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <S.FilterSelect
            value={filters.site}
            onChange={(e) => handleFilterChange('site', e.target.value)}
          >
            <option value="">Все сайты</option>
            <option value="hudle">Hudle</option>
            <option value="tournament">Турнирный сайт</option>
          </S.FilterSelect>
          <S.FilterSelect
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
          >
            <option value="">Все приоритеты</option>
            <option value="urgent">Срочно</option>
            <option value="high">Высокий</option>
            <option value="medium">Средний</option>
            <option value="low">Низкий</option>
          </S.FilterSelect>
        </S.HeaderActions>
      </S.DashboardHeader>

      <S.StatsBar>
        <S.StatItem>
          <S.StatValue>{stats.total}</S.StatValue>
          <S.StatLabel>Всего</S.StatLabel>
        </S.StatItem>
        <S.StatItem>
          <S.StatValue $color="#3B82F6">{stats.new}</S.StatValue>
          <S.StatLabel>Новые</S.StatLabel>
        </S.StatItem>
        <S.StatItem>
          <S.StatValue $color="#10B981">{stats.active}</S.StatValue>
          <S.StatLabel>В работе</S.StatLabel>
        </S.StatItem>
        <S.StatItem>
          <S.StatValue $color="#F59E0B">{stats.waitingResponse}</S.StatValue>
          <S.StatLabel>Ожидают ответа</S.StatLabel>
        </S.StatItem>
        <S.StatItem>
          <S.StatValue $color="#8B5CF6">{stats.resolved}</S.StatValue>
          <S.StatLabel>Решено</S.StatLabel>
        </S.StatItem>
        <S.StatItem>
          <S.StatValue $color="#6B7280">{stats.closed}</S.StatValue>
          <S.StatLabel>Закрыто</S.StatLabel>
        </S.StatItem>
      </S.StatsBar>

      {loading ? (
        <S.LoadingContainer>
          <S.LoadingText>Загрузка тикетов...</S.LoadingText>
        </S.LoadingContainer>
      ) : (
        <S.KanbanBoard>
          {kanbanColumns.map(column => {
            const columnTickets = getTicketsForColumn(column);
            const isDraggedOver = draggedOverColumn === column.id;
            return (
              <S.KanbanColumn
                key={column.id}
                $isDraggedOver={isDraggedOver}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column)}
              >
                <S.ColumnHeader>
                  <S.ColumnTitle>{column.label}</S.ColumnTitle>
                  <S.ColumnCount>{columnTickets.length}</S.ColumnCount>
                </S.ColumnHeader>
                <S.ColumnContent>
                  {columnTickets.length === 0 ? (
                    <S.EmptyColumn>Нет тикетов</S.EmptyColumn>
                  ) : (
                    columnTickets.map(ticket => (
                      <S.TicketCard
                        key={ticket.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, ticket)}
                        onClick={() => handleTicketClick(ticket.id)}
                      >
                        <S.CardHeader>
                          <S.CardDate>{formatDate(ticket.updatedAt || ticket.createdAt)}</S.CardDate>
                          <S.PriorityTag $color={getPriorityColor(ticket.priority)}>
                            {getPriorityLabel(ticket.priority)}
                          </S.PriorityTag>
                        </S.CardHeader>
                        <S.CardTitle>{ticket.title}</S.CardTitle>
                        <S.CardSubtitle>{ticket.client.name}</S.CardSubtitle>
                        <S.CardFooter>
                          <S.SiteBadge $site={ticket.site}>
                            {ticket.site === 'hudle'
                              ? 'Hudle'
                              : ticket.site === 'unknown'
                                ? 'Неизвестен'
                                : 'Турнир'}
                          </S.SiteBadge>
                          {ticket.ticketTypeName && (
                            <S.CategoryBadge>{ticket.ticketTypeName}</S.CategoryBadge>
                          )}
                          {ticket.categoryName && !ticket.ticketTypeName && (
                            <S.CategoryBadge>{ticket.categoryName}</S.CategoryBadge>
                          )}
                          {ticket.assignedTo && (
                            <S.AssignedUser>
                              {ticket.assignedTo}
                            </S.AssignedUser>
                          )}
                        </S.CardFooter>
                        {ticket.hasUnreadMessages && <S.UnreadDot />}
                      </S.TicketCard>
                    ))
                  )}
                </S.ColumnContent>
              </S.KanbanColumn>
            );
          })}
        </S.KanbanBoard>
      )}
    </S.DashboardContainer>
  );
};
